// frontend/src/components/auth/Login.tsx - UPDATED WITH PIN LOGIN OPTION
import React, { useState, useEffect } from 'react';
import { authService, AuthUser } from '../../services/auth.service';
import ForgotPasswordModal from './ForgotPasswordModal';
import styles from './Login.module.css';

interface LoginProps {
  onLoginSuccess: (user: AuthUser) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'pin'>('email');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [pin, setPin] = useState('');
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      if (user) {
        onLoginSuccess(user);
      }
    });

    return () => unsubscribe();
  }, [onLoginSuccess]);

  // Load remembered email if exists
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setUsername(rememberedEmail);
      setKeepLoggedIn(true);
    }
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Set persistence based on "Remember Me" checkbox
      await authService.setAuthPersistence(keepLoggedIn);
      
      // Login
      const user = await authService.login(username, password);
      
      // Store email if "Remember Me" is checked
      if (keepLoggedIn) {
        localStorage.setItem('rememberedEmail', username);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      onLoginSuccess(user);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!employeeNumber || !pin) {
      setError('Please enter employee number and PIN');
      return;
    }

    if (pin.length < 4 || pin.length > 6 || !/^\d+$/.test(pin)) {
      setError('PIN must be 4-6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Set persistence based on "Remember Me" checkbox
      await authService.setAuthPersistence(keepLoggedIn);
      
      // Login with PIN
      const user = await authService.loginWithPin(employeeNumber, pin);
      
      // Store employee number if "Remember Me" is checked
      if (keepLoggedIn) {
        localStorage.setItem('rememberedEmployeeNumber', employeeNumber);
      } else {
        localStorage.removeItem('rememberedEmployeeNumber');
      }
      
      onLoginSuccess(user);
    } catch (error: any) {
      setError(error.message || 'Invalid employee number or PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginMethod === 'email') {
      await handleEmailLogin(e);
    } else {
      await handlePinLogin(e);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPin = () => {
    alert('Please contact HR or your manager to reset your PIN.');
  };

  const handleSignUp = () => {
    alert('Sign Up functionality will be implemented soon.');
  };

  // Load remembered employee number for PIN login
  useEffect(() => {
    if (loginMethod === 'pin') {
      const rememberedEmpNo = localStorage.getItem('rememberedEmployeeNumber');
      if (rememberedEmpNo) {
        setEmployeeNumber(rememberedEmpNo);
        setKeepLoggedIn(true);
      }
    }
  }, [loginMethod]);

  return (
    <>
      <div 
        className={styles.loginContainer}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/backimg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className={styles.loginContent}>
          {/* User Icon */}
          <div className={styles.userIcon}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/humanicon.png`}
              alt="User Avatar"
              className={styles.userImage}
            />
          </div>

          {/* Login Title */}
          <h1 className={styles.loginTitle}>Login</h1>

          {/* Login Method Toggle */}
          <div className={styles.loginMethodToggle}>
            <button
              type="button"
              className={`${styles.loginMethodButton} ${loginMethod === 'email' ? styles.active : ''}`}
              onClick={() => setLoginMethod('email')}
            >
              Email Login
            </button>
            <button
              type="button"
              className={`${styles.loginMethodButton} ${loginMethod === 'pin' ? styles.active : ''}`}
              onClick={() => setLoginMethod('pin')}
            >
              PIN Login
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className={styles.loginForm}>
            
            {loginMethod === 'email' ? (
              <>
                {/* Username Field */}
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <span className={styles.inputIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#4A90E2" strokeWidth="2"/>
                        <circle cx="12" cy="7" r="4" stroke="#4A90E2" strokeWidth="2"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                      className={styles.inputField}
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <span className={styles.inputIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#4A90E2" strokeWidth="2"/>
                        <circle cx="12" cy="16" r="1" fill="#4A90E2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#4A90E2" strokeWidth="2"/>
                      </svg>
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className={styles.inputField}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#999" strokeWidth="2"/>
                        <circle cx="12" cy="12" r="3" stroke="#999" strokeWidth="2"/>
                        {!showPassword && <path d="M3 3l18 18" stroke="#999" strokeWidth="2"/>}
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Login Options */}
                <div className={styles.loginOptions}>
                  <label className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={keepLoggedIn}
                      onChange={(e) => setKeepLoggedIn(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>keep me logged in</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className={styles.forgotPassword}
                    disabled={loading}
                  >
                    Forgot Password?
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Employee Number Field */}
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <span className={styles.inputIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#4A90E2" strokeWidth="2"/>
                        <circle cx="12" cy="7" r="4" stroke="#4A90E2" strokeWidth="2"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Employee Number"
                      value={employeeNumber}
                      onChange={(e) => setEmployeeNumber(e.target.value.replace(/\D/g, ''))}
                      disabled={loading}
                      className={styles.inputField}
                      required
                    />
                  </div>
                </div>

                {/* PIN Field */}
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <span className={styles.inputIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#4A90E2" strokeWidth="2"/>
                        <circle cx="12" cy="16" r="1" fill="#4A90E2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#4A90E2" strokeWidth="2"/>
                      </svg>
                    </span>
                    <input
                      type="password"
                      placeholder="6-digit PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={loading}
                      className={styles.inputField}
                      maxLength={6}
                      required
                    />
                    <div className={styles.pinLength}>
                      {pin.length}/6
                    </div>
                  </div>
                </div>

                {/* Login Options for PIN */}
                <div className={styles.loginOptions}>
                  <label className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={keepLoggedIn}
                      onChange={(e) => setKeepLoggedIn(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>keep me logged in</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleForgotPin}
                    className={styles.forgotPassword}
                    disabled={loading}
                  >
                    Forgot PIN?
                  </button>
                </div>
              </>
            )}

            {/* Login Button */}
            <button 
              type="submit" 
              className={styles.loginButton}
              disabled={loading}
            >
              {loading ? 'Logging In...' : 'Login'}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className={styles.signupLink}>
            <span>Do you have an account? </span>
            <button
              type="button"
              onClick={handleSignUp}
              className={styles.signupButton}
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </>
  );
};

export default Login;